export interface RfqExcelItem {
  id: string;
  impaNo: string;
  itemDescription: string;
  drawingNumber: string;
  partNo: string;
  altPartNo: string;
  modelSerial?: string;
  positionNo: string;
  dimensions: string;
  requiredQuantity: string;
  uom: string;
  quality: string;
  generalRemark: string;
  deliveryPriority: string;
  expectedDeliveryDate?: string;
}

export interface RfqExcelFormData {
  rfqStatus: string;
  revisionNo: string;
  currency: string;
  quoteDeadline: string;
  quoteValidityDays: string;
  urgencyLevel: string;
  requisitionRef: string;
  vesselName: string;
  imoNo: string;
  equipmentTags: string;
  category: string;
  subCategory: string;
  brand: string;
  model: string;
  department: '' | 'engine' | 'deck' | 'electrical' | 'navigation' | 'safety' | 'environmental' | 'cabin' | 'operations' | 'other';
  estimatedTotalCost: string;
  remarks: string;
  deliveryRequiredAt: string;
  deliveryType: string;
  portId: string;
  portName: string;
  portCountry: string;
  portEtaDate: string;
  portAgentId: string;
  portAgentName: string;
  portAddress: string;
  portLatitude: string;
  portLongitude: string;
  vesselCurrentLocation: string;
  vesselLatitude: string;
  vesselLongitude: string;
  vesselNextPort: string;
  vesselEtaWindow: string;
  vesselInstructions: string;
  creditType: string;
  paymentTerm: string;
  insuranceTerm: string;
  taxTerm: string;
  transportTerm: string;
  deliveryTerm: string;
  packingTerm: string;
  createdDate: string;
  leadDate: string;
  partialQuoteAllowed: string;
  splitOrderAllowed: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactDepartment: string;
  requiredPaymentTerms: string;
  requiredCertificates: string[];
  requiredDocuments: string[];
  packingInstructions: string;
  partialDeliveryAllowed: string;
  selectedVendorIds: string[];
}

export interface RfqExcelLookupData {
  vessels: { _id: string; name: string; imoNumber?: string }[];
  categories: { _id: string; name: string }[];
  vendors: { _id: string; name: string }[];
  ports: { _id: string; name: string; country: string }[];
  portAgents?: { _id: string; companyName: string; address?: string; portId?: string }[];

  /** Hierarchical master-data maps for import resolution (optional). */
  subCategoriesByCategoryId?: Record<string, { _id: string; name: string }[]>;
  brandsByCategoryId?: Record<string, { _id: string; name: string }[]>;
  modelsByBrandId?: Record<string, { _id: string; name: string }[]>;
  portAgentsByPortId?: Record<string, { _id: string; companyName: string; address?: string }[]>;

  /** Lazy GET loaders (reuse existing APIs; never create). */
  getSubCategoriesForCategory?: (
    categoryId: string
  ) => Promise<{ _id: string; name: string }[]> | { _id: string; name: string }[];
  getBrandsForCategory?: (
    categoryId: string
  ) => Promise<{ _id: string; name: string }[]> | { _id: string; name: string }[];
  getModelsForBrand?: (
    brandId: string
  ) => Promise<{ _id: string; name: string }[]> | { _id: string; name: string }[];
  getPortAgentsForPort?: (
    portId: string
  ) =>
    | Promise<{ _id: string; companyName: string; address?: string }[]>
    | { _id: string; companyName: string; address?: string }[];

  /** Preloaded PartFinder registry rows (tests / offline). */
  partFinderParts?: import('./rfq-import-partfinder-resolve').RfqPartFinderRecord[];
  /** Lazy GET of admin PartFinder registry (read-only). */
  getPartFinderParts?: () =>
    | Promise<import('./rfq-import-partfinder-resolve').RfqPartFinderRecord[]>
    | import('./rfq-import-partfinder-resolve').RfqPartFinderRecord[];
}

export interface RfqExcelImportPreview {
  formPatch: Partial<RfqExcelFormData>;
  items: RfqExcelItem[];
  warnings: string[];
  errors: string[];
  summary: {
    itemCount: number;
    vendorCount: number;
    matchedFields: string[];
    unmatchedFields: string[];
    /** Required RFQ fields with no usable value in the source workbook. */
    fieldsNotProvided?: string[];
    /** Required RFQ fields present in source but not resolved to ERP masters / form. */
    fieldsUnresolved?: Array<{ field: string; providedValue: string }>;
    /** Offer/quotation columns detected but unsupported by RFQ item model. */
    unsupportedQuotationFields?: string[];
  };
  /** Optional master-data resolution details for preview / PDF reuse. */
  resolutions?: Array<{
    field: string;
    importedValue: string;
    resolvedValue?: string;
    resolvedId?: string;
    status: string;
    message?: string;
  }>;
  /**
   * Raw header values exactly as extracted from the source document, keyed by
   * review field (rfqNumber, quoteDeadline, department, vessel, category, subCategory,
   * brand, model, vendors, port, portAgent). Kept even when ERP resolution fails.
   */
  extractedFields?: Partial<Record<string, string>>;
  /** Validate-only PartFinder item resolutions (does not alter items). */
  partfinderResolutions?: import('./rfq-import-partfinder-resolve').RfqPartFinderItemResolution[];
  /** Import source for shared review UI (Excel vs PDF/IDP). */
  source?: 'excel' | 'pdf';
  /** Existing IDP overall confidence (0–1 or percent); Excel omits this. */
  overallConfidence?: number;
  /** Prefer manual review before apply (IDP + unresolved master/PartFinder). */
  needsManualReview?: boolean;
}

export interface RfqExcelHeaderField {
  label: string;
  key: keyof RfqExcelFormData | 'vendors' | 'portNameInput' | 'portAgentNameInput';
  required: boolean;
  hint?: string;
  sample?: string;
}

export const RFQ_HEADER_FIELDS: RfqExcelHeaderField[] = [
  { label: 'Quote Deadline', key: 'quoteDeadline', required: true, hint: 'YYYY-MM-DD', sample: '2026-06-15' },
  { label: 'Department', key: 'department', required: true, hint: 'engine, deck, cabin, operations, safety, other', sample: 'engine' },
  { label: 'Vessel Name', key: 'vesselName', required: true, hint: 'Match fleet registry name or IMO', sample: 'MV ATLANTIC' },
  { label: 'Category', key: 'category', required: true, sample: 'Engine Parts' },
  { label: 'Brand', key: 'brand', required: true, sample: 'Caterpillar' },
  { label: 'Model', key: 'model', required: true, sample: 'C3516' },
  { label: 'Vendors', key: 'vendors', required: true, hint: 'Comma-separated vendor names', sample: 'Vendor A, Vendor B' },
  { label: 'Currency', key: 'currency', required: false, hint: 'USD, EUR, INR, AED, SGD', sample: 'USD' },
  { label: 'Urgency', key: 'urgencyLevel', required: false, hint: 'Normal, High, Critical', sample: 'Normal' },
  { label: 'Quote Validity Days', key: 'quoteValidityDays', required: false, hint: '15, 30, or 60', sample: '30' },
  { label: 'Requisition Ref', key: 'requisitionRef', required: false, sample: 'REQ-2026-001' },
  { label: 'Equipment Tags', key: 'equipmentTags', required: false, sample: 'MAIN ENGINE, PUMP' },
  { label: 'Sub Category', key: 'subCategory', required: false, sample: 'Piston Rings' },
  { label: 'Estimated Total Cost', key: 'estimatedTotalCost', required: false, sample: '5000' },
  { label: 'Remarks', key: 'remarks', required: false, sample: 'Emergency spare parts required' },
  { label: 'Contact Name', key: 'contactName', required: false, sample: 'John Smith' },
  { label: 'Contact Email', key: 'contactEmail', required: false, sample: 'john@company.com' },
  { label: 'Contact Phone', key: 'contactPhone', required: false, sample: '+1 555 0100' },
  { label: 'Contact Department', key: 'contactDepartment', required: false, hint: 'Procurement, Technical, Operations', sample: 'Procurement' },
  { label: 'Delivery Required At', key: 'deliveryRequiredAt', required: false, hint: 'port', sample: 'port' },
  { label: 'Delivery Type', key: 'deliveryType', required: false, hint: 'berth, anchorage, warehouse', sample: 'berth' },
  { label: 'Port Name', key: 'portNameInput', required: false, hint: 'Supply / delivery port name', sample: 'Singapore' },
  { label: 'Port ETA Date', key: 'portEtaDate', required: false, hint: 'YYYY-MM-DD', sample: '2026-06-20' },
  { label: 'Port Agent Name', key: 'portAgentNameInput', required: false, sample: 'ABC Port Agency' },
  { label: 'Lead Date', key: 'leadDate', required: false, hint: 'YYYY-MM-DD', sample: '2026-06-25' },
  { label: 'Credit Type', key: 'creditType', required: false, sample: 'Open Credit' },
  { label: 'Payment Term', key: 'paymentTerm', required: false, sample: 'Net 30 Days' },
  { label: 'Insurance Term', key: 'insuranceTerm', required: false, sample: "Seller's Responsibility" },
  { label: 'Tax Term', key: 'taxTerm', required: false, sample: 'GST Extra' },
  { label: 'Transport Term', key: 'transportTerm', required: false, sample: 'Sea Freight' },
  { label: 'Delivery Term', key: 'deliveryTerm', required: false, sample: '7–14 Days' },
  { label: 'Packing Term', key: 'packingTerm', required: false, sample: 'Standard Packing' },
  { label: 'Payment Terms', key: 'requiredPaymentTerms', required: false, sample: '30 Days Credit' },
  { label: 'Required Certificates', key: 'requiredCertificates', required: false, hint: 'Comma-separated: Class, OEM, COC, MTC', sample: 'Class, OEM' },
  { label: 'Required Documents', key: 'requiredDocuments', required: false, hint: 'Comma-separated', sample: 'Commercial Invoice, Packing List' },
  { label: 'Packing Instructions', key: 'packingInstructions', required: false, sample: 'Wooden crate, moisture protection' },
  { label: 'Partial Delivery Allowed', key: 'partialDeliveryAllowed', required: false, hint: 'yes or no', sample: 'yes' },
];

export const RFQ_ITEM_COLUMNS = [
  { header: 'Description', key: 'itemDescription', required: true },
  { header: 'Quantity', key: 'requiredQuantity', required: true },
  { header: 'UOM', key: 'uom', required: true },
  { header: 'Quality', key: 'quality', required: false },
  { header: 'Drawing No', key: 'drawingNumber', required: false },
  { header: 'Part No', key: 'partNo', required: false },
  { header: 'IMPA No', key: 'impaNo', required: false },
  { header: 'Alt Part No', key: 'altPartNo', required: false },
  { header: 'Position No', key: 'positionNo', required: false },
  { header: 'Model / Serial', key: 'modelSerial', required: false },
  { header: 'Dimensions', key: 'dimensions', required: false },
  { header: 'Priority', key: 'deliveryPriority', required: false },
  { header: 'Expected Delivery Date', key: 'expectedDeliveryDate', required: false },
  { header: 'Remarks', key: 'generalRemark', required: false },
] as const;

export const VALID_UOMS = ['Pcs', 'Kgs', 'Ltrs', 'Mtrs', 'Sets', 'Box'];
export const VALID_PRIORITIES = ['normal', 'urgent', 'critical'];
export const VALID_QUALITIES = ['Genuine', 'OEM', 'Equivalent', 'Copy Parts'];
export const VALID_DEPARTMENTS = [
  'engine',
  'deck',
  'electrical',
  'navigation',
  'safety',
  'environmental',
  'cabin',
  'operations',
  'other',
];
